import { api } from './api'

// Web Push enrollment: register the service worker, ask permission, subscribe
// with the server's VAPID key, and hand the subscription to the API.
//
// Deliberately not run automatically on every load — requesting notification
// permission the instant someone signs in is exactly the pattern browsers
// have trained users to reflexively deny. It runs from an explicit "Enable
// notifications" click instead, so the permission prompt has context.

/** pushSupported reports whether this browser can do any of this at all. */
export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/**
 * PushState is what the UI actually needs to decide what to show and what a
 * click should do — which is not the same question as "what does
 * Notification.permission say".
 *
 * That distinction is the whole reason this type exists rather than the UI
 * reading Notification.permission directly: permission can be 'granted' with
 * no subscription ever having been created — the user granted it on a
 * previous visit, or (as this exact scenario surfaced in testing) a browser
 * profile with the origin pre-authorised — and treating 'granted' as "already
 * on" would make a click call unsubscribe on a subscription that was never
 * created, silently doing nothing.
 */
export type PushState = 'unsupported' | 'blocked' | 'off' | 'on'

/** pushState is the ground truth: unsupported/blocked come from the
 *  Permission API, but 'on' requires an actual PushSubscription to exist, not
 *  merely permission to create one. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'blocked'

  // A registration with no subscription is 'off' even when permission is
  // already 'granted' — see the PushState doc comment above.
  const registration = await navigator.serviceWorker.getRegistration('/')
  const subscription = await registration?.pushManager.getSubscription()
  return subscription ? 'on' : 'off'
}

/** base64URLToUint8Array decodes the VAPID public key into the byte form
 *  PushManager.subscribe requires — it hands back a base64url string, and the
 *  browser API wants raw bytes. */
function base64URLToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  // Backed by a concrete ArrayBuffer rather than the plain Uint8Array
  // constructor's ArrayBufferLike, which newer TS DOM typings no longer widen
  // to the BufferSource applicationServerKey expects.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/**
 * enablePush walks the whole flow and returns the resulting subscription.
 *
 * Safe to call when permission is already granted (from a previous visit, or
 * granted by other means) — requestPermission on an already-decided
 * permission resolves immediately with the existing value rather than
 * prompting again, so this does not double-ask.
 *
 * Throws with a message meant to be shown directly to the user — "permission
 * denied" and "not supported" are both things a person can understand and
 * act on, unlike a raw DOMException.
 */
export async function enablePush(): Promise<PushSubscription> {
  if (!pushSupported()) {
    throw new Error('This browser does not support push notifications.')
  }

  const registration = await navigator.serviceWorker.register('/sw.js')
  // register() can resolve before the worker is actually controlling the
  // page on a first-ever visit; subscribe() only needs it registered, not
  // active, so this is not awaited further.

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.')
  }

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    const { public_key: publicKey } = await api.vapidPublicKey()
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true, // required by the spec: every push must show something
      applicationServerKey: base64URLToUint8Array(publicKey),
    })
  }

  await api.subscribePush(subscription.toJSON() as PushSubscriptionJSON, deviceLabel())
  return subscription
}

/** disablePush unsubscribes locally and tells the server to forget the device. */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return
  const registration = await navigator.serviceWorker.getRegistration('/')
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) return

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  await api.unsubscribePush(endpoint)
}

/** deviceLabel is a human-readable hint shown wherever a user's devices might
 *  eventually be listed — "Chrome on Linux" beats an opaque device id. */
function deviceLabel(): string {
  const ua = navigator.userAgent
  const browser = /Firefox/.test(ua) ? 'Firefox' : /Edg\//.test(ua) ? 'Edge' : /Chrome/.test(ua) ? 'Chrome' : /Safari/.test(ua) ? 'Safari' : 'Browser'
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : ''
  return os ? `${browser} on ${os}` : browser
}
